// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — the bundled country dataset (Natural Earth 1:110m, public domain) and
// the pool/search helpers over it. Loaded at build time, fully offline.

import raw from './data/countries.json';
import type { LonLat } from './geo';

export type Pool = 'core' | 'extra' | 'territory';

export interface Country {
  id: string;
  name: string;
  /** Continent label from Natural Earth. */
  cont: string;
  /** Centroid / label point [lon, lat]. */
  c: LonLat;
  pool: Pool;
  pop: number;
  /** Simplified exterior rings, each a flat [lon,lat,lon,lat,…] array. */
  rings: number[][];
}

interface RawData {
  source: string;
  sourceUrl: string;
  countries: Country[];
}

const DATA = raw as unknown as RawData;

export const SOURCE = DATA.source;
export const SOURCE_URL = DATA.sourceUrl;

/** Every country, sorted by name (the generator already sorts). */
export const ALL: readonly Country[] = DATA.countries;

export const BY_ID: ReadonlyMap<string, Country> = new Map(ALL.map((c) => [c.id, c]));

export function country(id: string): Country | undefined {
  return BY_ID.get(id);
}

/** Normalise a name for search / matching: lowercase, strip accents + punctuation. */
export function normalizeName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .trim();
}

/** Common alternative names people type, mapped to the canonical id-name. */
const ALIASES: Record<string, string> = {
  usa: 'United States',
  us: 'United States',
  america: 'United States',
  uk: 'United Kingdom',
  britain: 'United Kingdom',
  england: 'United Kingdom',
  uae: 'United Arab Emirates',
  drc: 'DR Congo',
  'south korea': 'South Korea',
  'north korea': 'North Korea',
  'ivory coast': "Côte d'Ivoire",
  burma: 'Myanmar',
  swaziland: 'Eswatini',
  'czech republic': 'Czechia',
  macedonia: 'North Macedonia',
  holland: 'Netherlands',
  'the netherlands': 'Netherlands',
};

const NORM_INDEX: Map<string, Country> = (() => {
  const m = new Map<string, Country>();
  for (const c of ALL) m.set(normalizeName(c.name), c);
  return m;
})();

/** Resolve a typed string to a country in `pool`, honouring aliases. Null if none. */
export function resolveTyped(input: string, pool: ReadonlySet<string>): Country | null {
  const n = normalizeName(input);
  if (!n) return null;
  const aliasTarget = ALIASES[n];
  const direct = aliasTarget ? NORM_INDEX.get(normalizeName(aliasTarget)) : NORM_INDEX.get(n);
  if (direct && pool.has(direct.id)) return direct;
  // Fall back to a unique prefix match within the pool.
  const hits = ALL.filter((c) => pool.has(c.id) && normalizeName(c.name).startsWith(n));
  return hits.length === 1 ? hits[0] : null;
}

/** Ranked search results within a pool: exact > alias > prefix > substring. */
export function searchCountries(
  input: string,
  pool: ReadonlySet<string>,
  limit = 8,
): Country[] {
  const n = normalizeName(input);
  if (!n) return [];
  const alias = ALIASES[n];
  const aliasId = alias ? NORM_INDEX.get(normalizeName(alias))?.id : undefined;
  const scored: Array<{ c: Country; s: number }> = [];
  for (const c of ALL) {
    if (!pool.has(c.id)) continue;
    const name = normalizeName(c.name);
    let s = Infinity;
    if (name === n) s = 0;
    else if (c.id === aliasId) s = 1;
    else if (name.startsWith(n)) s = 2;
    else if (name.includes(n)) s = 3;
    else if (n.length >= 3 && name.split(' ').some((w) => w.startsWith(n))) s = 4;
    if (s < Infinity) scored.push({ c, s });
  }
  scored.sort((a, b) => a.s - b.s || a.c.name.localeCompare(b.c.name));
  return scored.slice(0, limit).map((x) => x.c);
}
